import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FinanceEntryFields, { isFinanceEntryValid } from "@/components/FinanceEntryFields";
import { useAuth } from "@/context/AuthContext";

const EMPTY_DRAFT = {
  date: new Date().toISOString().slice(0, 10),
  amount: "", project_id: "", project_name: "", description: "",
  department: "", category: "", sub_category: "", cost_type: "", aop: "",
  vendor_id: "", vendor: "", payment_mode: "", source_id: "",
  drive_link: "", month: new Date().toISOString().slice(0, 7),
};

/**
 * Standalone "Add expense" dialog — the exact same form the Expenses Ledger
 * uses (`FinanceEntryFields` fetches its own projects/vendors/categories/
 * sources, so this needs no data props). Extracted so the Home Command
 * Center's "New expense" shortcut and the Finance module's own "Add expense"
 * button share one single form instead of two copies.
 */
export default function AddExpenseDialog({ open, onOpenChange, onSaved }) {
  const { user } = useAuth();
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  useEffect(() => { if (!open) setDraft(EMPTY_DRAFT); }, [open]);

  const isValid = useMemo(
    () => isFinanceEntryValid({ ...draft, amount: Number(draft.amount || 0) }, { role: user?.role }) && (draft.description || "").trim(),
    [draft, user?.role]
  );

  const save = async () => {
    if (!isValid) return;
    try {
      await api.post("/finance/expenses", { ...draft, amount: Number(draft.amount) });
      onOpenChange(false);
      toast.success("Expense added");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="expense-add-dialog">
        <DialogHeader><DialogTitle className="font-display">Add expense</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label-overline">Amount ₹ *</label>
              <Input type="number" min="0" className="rounded-sm mt-1" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} data-testid="expense-add-amount" />
            </div>
            <div>
              <label className="label-overline">Date *</label>
              <Input type="date" className="rounded-sm mt-1" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} data-testid="expense-add-date" />
            </div>
          </div>
          <div>
            <label className="label-overline">Narration / Description *</label>
            <Input className="rounded-sm mt-1" value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="expense-add-desc" />
          </div>
          <div className="pt-3 border-t border-slate-200">
            <FinanceEntryFields value={draft} onChange={(p) => setDraft({ ...draft, ...p })} testidPrefix="expense" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={!isValid} data-testid="expense-add-save"
            className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed">
            Save expense
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
